'use client';

import { useState } from 'react';
import { useRouter } from '@/i18n/routing';
import { deleteFootprint } from "@/lib/actions/footprints";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { MediaImg } from '@/components/shared/MediaImg';
import { Edit, Trash2, Plus, Target } from "lucide-react";
import { Link } from "@/i18n/routing";

interface FootprintListProps {
  footprints: any[];
}

export function FootprintList({ footprints }: FootprintListProps) {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const router = useRouter();

  const handleDelete = async (id: string) => {
    setIsLoading(id);
    try {
      const result = await deleteFootprint(id);
      if (result.success) {
        toast.success("Footprint deleted successfully");
        router.refresh();
      } else {
        toast.error(result.error || "Failed to delete footprint");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>All Footprints / Industries</CardTitle>
        <Button asChild className="bg-navy hover:bg-primary transition-all">
          <Link href="/admin/footprints/new">
            <Plus className="mr-2 h-4 w-4" /> Add New
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Image</TableHead>
              <TableHead>Industry Title</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {footprints.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-10 text-gray-400">
                  <Target className="mx-auto h-10 w-10 mb-2 opacity-20" />
                  <p>No footprints found. Add your first industry footprint!</p>
                </TableCell>
              </TableRow>
            ) : (
              footprints.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.image ? (
                      <div className="w-12 h-12 rounded-lg border overflow-hidden">
                        <MediaImg src={item.image} alt={item.title} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-light flex items-center justify-center text-gray-400">
                        <Target size={20} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{item.title}</TableCell>
                  <TableCell>{item.order}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="icon" asChild>
                        <Link href={`/admin/footprints/${item.id}`}>
                          <Edit className="h-4 w-4" />
                        </Link>
                      </Button>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete the <strong>{item.title}</strong> industry footprint.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(item.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {isLoading === item.id ? 'Deleting...' : 'Delete'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
